/**
 * Used to define boundaries and set a locations position.
 * https://github.com/angular/components/tree/main/src/google-maps
 * Various inputs are defined and can be used to allow for the setting
 * of boundaries or just for position, see the input comments below.
 *
 * PENDING IMPROVEMENTS:
 * - Address Fields
 * - Implement Find out more button
 * - Fix the custom markers
 * - Click Custom Boundary and Allow us to Edit it
 * - Update point and boundary in the same object and emit once.
 * - Let the parent update the location and send the data back to the child for immediate update response
 * - Standalone save button
 */

import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { GoogleMap, MapGeocoder } from '@angular/google-maps';
import {
  IGeography,
  ILocation,
  ILocationAddress,
} from '@boma/supplier-portal/shared/entities';
import {
  CountriesService,
  HelperFunctionsService,
} from '@boma/supplier-portal/shared/services';
import { MapsService } from '@boma/website/shared';

declare const google: any;

@Component({
  selector: 'boma-update-location',
  templateUrl: './boma-update-location.html',
  styleUrls: ['./boma-update-location.scss'],
})
export class BomaUpdateLocationComponent implements OnInit {
  private googleMapInstance: GoogleMap; // Declare a private variable to store the reference

  @ViewChild(GoogleMap, { static: false }) set googleMap(m: GoogleMap) {
    if (m) {
      this.googleMapInstance = m; // Store the reference in googleMapInstance
      // if (this.allowDrawing) this.initDrawingManager(m);
    }
  }

  @ViewChild('currentLocation') currentLocation;

  @Input() country = null;
  @Input() showBoundary = false; // Explicity request the showing of boundary
  @Input() pickerOnly = false;
  @Input() allowDrawing = true; // Allow drawing of boundaries
  @Input() allowEdit = false; // Allow for editing of geo data
  @Input() update = false;
  @Input() location: ILocation = null;
  @Input() mapOptions: google.maps.MapOptions = {
    center: { lat: 7, lng: 21 },
    zoom: 2,
    streetViewControl: false,
  };
  @Input() markers = [];
  @Input() multiple = false; // if we have multiple or single marker logic...

  @Output() setGeoData: EventEmitter<{
    point: IGeography;
    boundary: IGeography;
  }> = new EventEmitter();
  @Output() setPoint = new EventEmitter();
  @Output() setPolygon = new EventEmitter();

  // Need to get the countries default latlong for a better user experience
  // Set to the location || device location || country default
  latitude = 7;
  longitude = 21;

  findingMe = false;

  userSelectedCoordinates: {
    long: number;
    lat: number;
  } = {
    long: null,
    lat: null,
  };

  center: google.maps.LatLngLiteral = { lat: 7, lng: 21 };

  loadedMapsApi: boolean;

  boundary: any;

  polygonOptions: google.maps.PolygonOptions = {
    fillColor: '#4184EA',
    fillOpacity: 0.4, // (0 to 1)
    strokeColor: '#003F9E',
    strokeWeight: 1,
    clickable: false,
    draggable: false,
    editable: false,
    geodesic: false,
    zIndex: 1,
  };

  drawingManager: any;

  selectedArea = 0;
  countryAreas: any = [];
  selectedCountryArea: any = [];

  newAddress: ILocationAddress = {
    line1: '',
    line2: '',
    line3: '',
    line4: '',
    town: '',
    province: '',
    postalCode: '',
    id: '',
  };

  formattedAddress: string[];

  customBoundary: any;
  customPoint: any;

  help = false;
  helpSlides = [
    {
      image: '/assets/layout/images/favi/apple-icon-144x144.png',
      title: 'Geo Fenced to Country',
      text: 'All boundaries should be contained within the country, and cannot cross the countries borders.',
    },
    {
      image: '/assets/layout/images/favi/apple-icon-144x144.png',
      title: 'Full Screen',
      text: 'Click the icon in the top right hand corner\nof the map to enable full screen for easier boundary setting.',
    },
    {
      image: '/assets/layout/images/favi/apple-icon-144x144.png',
      title: 'Drawing',
      text: 'Click the hand icon at the top of the map to\nstop drawing or just edit your existing area..',
    },
    {
      image: '/assets/layout/images/favi/apple-icon-144x144.png',
      title: 'Right Click to Delete Points',
      text: 'Right click primary points on the map to\n delete them.',
    },
    {
      image: '/assets/layout/images/favi/apple-icon-144x144.png',
      title: 'Move Map',
      text: 'Hold right click down to drag around the map.',
    },
  ];

  editAddress = false;

  constructor(
    public mapsService: MapsService,
    private countrySvc: CountriesService,
    private geocoder: MapGeocoder,
    private helperService: HelperFunctionsService
  ) {}

  ngOnInit(): void {
    // If we have a distribution boundary, we render it on the map.
    // Note that boundary is rendered conditionally on in the html with a map-polygon tag
    if (this.showBoundary && this.location?.distributionBoundary?.coordinates) {
      this.boundary = this.convertCoordinates(
        this.location.distributionBoundary.coordinates
      );

      this.boundary = this.boundary.pop(); // Google doesn't need last point (same as first), we need to put it back later.
    }

    // Grab the provinces for the selected country
    this.countrySvc.getJSON(this.country).subscribe((areas) => {
      if (areas?.data?.length) {
        this.countryAreas = areas.data;
      } else {
        this.countryAreas = []; // Provide a default value (an empty array) if areas is null or empty
      }
    });

    this.mapsService.loadedGoogleMapsApi$.subscribe((apiLoaded) => {
      this.loadedMapsApi = apiLoaded;

      if (this.loadedMapsApi) {
        if (this.location?.point) {
          this.center = {
            lat: this.location.point.coordinates[1],
            lng: this.location.point.coordinates[0],
          };
          this.latitude = this.location.point.coordinates[1];
          this.longitude = this.location.point.coordinates[0];
        }
      } else {
        // TODO Handle errors...
      }
    });
  }

  /**
   * Intialise the drawing manager, note you can just manipulate the drawing options
   * on the drawing manager to show and hide the tools as required once initialised.
   * @param map The instance of the google map
   * @returns
   */
  initDrawingManager(map: GoogleMap = this.googleMapInstance) {
    if (this.drawingManager) return; // we already have the tools.
    const drawingOptions = {
      drawingMode: google.maps.drawing.OverlayType.POLYGON,
      drawingControl: true,
      drawingControlOptions: {
        position: google.maps.ControlPosition.TOP_CENTER,
        drawingModes: [
          google.maps.drawing.OverlayType.POLYGON,
          // google.maps.drawing.OverlayType.CIRCLE,
        ],
      },
      polygonOptions: {
        strokeColor: '#00ff00',
        editable: true,
      },
    };
    this.drawingManager = new google.maps.drawing.DrawingManager(
      drawingOptions
    );
    this.drawingManager.setMap(map.googleMap);

    //TODO making the map go full screen at this point would be cool

    /**
     * Initiliases when the polygon shape has been completed (first and last point connected)
     */

    google.maps.event.addListener(
      this.drawingManager,
      'overlaycomplete',
      (event) => {
        if (event.type === google.maps.drawing.OverlayType.POLYGON) {
          this.customBoundary = event.overlay;
          this.convertLatLongToArray(this.customBoundary);

          google.maps.event.addListener(
            this.customBoundary.getPath(),
            'insert_at',
            (event) => {
              this.convertLatLongToArray(this.customBoundary);
            }
          );

          google.maps.event.addListener(
            this.customBoundary.getPath(),
            'set_at',
            (event) => {
              this.convertLatLongToArray(this.customBoundary);
            }
          );

          // Disable the drawing tools
          this.drawingManager.setOptions({
            drawingMode: null,
            drawingControl: false,
          });

          // Now, you can work with the 'polygon' object, which represents the drawn polygon.
          // You can store it, manipulate it, or do anything you need with it.
        }
      }
    );
  }

  // convertPolygonToCoords() {
  //   const coords = this.customBoundary
  //     .getPath()
  //     .getArray()
  //     .map((coord) => {
  //       return {
  //         lat: coord.lat(),
  //         lng: coord.lng(),
  //       };
  //     });
  //   console.log(JSON.stringify(coords, null, 1));
  //   return coords;
  // }

  /**
   * Convert tha lat long object array to a simple array
   * @param latLong
   */
  convertLatLongToArray(latLong) {
    const coordsArray = latLong.getPath().getArray();
    const coords = coordsArray.map((point) => [point.lng(), point.lat()]);
    coords.push(coords[0]); // IMPORTANT, Geospace will not resolve without the first and last point being the same
    this.setPolygon.emit(coords);
  }

  /**
   * Recursively convert long lat coordinates into latLong Json object
   * @param arr Array fo coordinates long lat
   * @returnsAn an Object array of LatLong
   */
  convertCoordinates(arr) {
    if (Array.isArray(arr)) {
      // Check if the array has exactly two elements and convert them if so
      if (
        arr.length === 2 &&
        typeof arr[0] === 'number' &&
        typeof arr[1] === 'number'
      ) {
        return { lat: arr[1], lng: arr[0] };
      }

      // If it's not a valid pair of coordinates, recursively process nested arrays
      return arr.map((item) => this.convertCoordinates(item));
    }

    return arr;
  }

  moveMap(event: google.maps.MapMouseEvent) {
    this.center = event.latLng.toJSON();
  }

  mapDoubleClicked(event) {
    const clickedLatLng = event.latLng;

    if (this.googleMapInstance.googleMap.getZoom() == 8) {
      this.googleMapInstance.googleMap.setZoom(4);
    } else {
      this.googleMapInstance.googleMap.setZoom(8);
    }

    this.googleMapInstance.googleMap.setCenter(clickedLatLng);
  }

  findCoordinates(
    lat = this.userSelectedCoordinates.lat,
    long = this.userSelectedCoordinates.long,
    zoom = 10
  ) {
    this.googleMapInstance.googleMap.setZoom(zoom);

    // console.log(lat, long);

    this.center = {
      lat: Number(lat),
      lng: Number(long),
    };
  }

  boundaryClicked(event: google.maps.MapMouseEvent) {
    // console.log('clicked the boundary', event);
  }

  mapClicked(event: google.maps.MapMouseEvent) {
    if (this.update) {
      // we just updating the current single location
      const latLong = event.latLng;

      this.latitude = latLong.lat();
      this.longitude = latLong.lng();

      // set the current pin to the event coordinates
      (this.markers[0] = {
        position: {
          lat: this.latitude,
          lng: this.longitude,
        },
        options: { draggable: true, animation: null },
      }),
        (this.center = {
          lat: latLong.lat(),
          lng: latLong.lng(),
        });
      this.getAddress(this.latitude, this.longitude);
      //this.emitPoint(); // We get the address to update it and the address look up emits the point.
    }
  }

  emitPoint() {
    this.setPoint.emit({
      point: [this.longitude, this.latitude],
      address: this.newAddress,
    });
  }

  markerDragEnd(event) {
    this.center = event.latLng.toJSON();
    this.emitPoint();
  }

  setCurrentPosition(lat = null, long = null) {
    if (this.findingMe) return;
    this.findingMe = true;

    if (lat && long) {
      this.center = {
        lat: lat,
        lng: long,
      };
      this.addMarker({ lat: this.center.lat, lng: this.center.lng });
      this.findingMe = false;
    } else {
      // No point passed; let's get the user's location from the device
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            this.latitude = position.coords.latitude;
            this.longitude = position.coords.longitude;

            this.center = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };

            this.googleMapInstance.googleMap.setZoom(10);

            // Lets drop a pin saying this is where you are...
            this.addMarker({
              lat: this.center.lat,
              lng: this.center.lng,
              label: 'You Are Here',
              drag: false,
            });

            this.emitPoint();
            this.findingMe = false;
          },
          (error) => {
            console.error('Error getting location:', error);
            this.findingMe = false;
          }
        );
      } else {
        console.error('Geolocation is not available in this browser.');
        this.findingMe = false;
      }
    }
  }

  addMarker({
    lat = this.center.lat,
    lng = this.center.lng,
    title = 'Title',
    label = 'Label',
    labelColor = 'black',
    drag = true,
  }: {
    lat?: number;
    lng?: number;
    title?: string;
    label?: string;
    labelColor?: string;
    drag?: boolean;
  } = {}): void {
    // Let's prevent adding any additional markers that already exist... maybe we can handle multiple ones.
    const hasMarker = this.markers.some((marker) => {
      return marker.position.lat === lat && marker.position.lng === lng;
    });

    if (!hasMarker) {
      this.markers.push({
        position: {
          lat: lat,
          lng: lng,
        },
        label: {
          color: labelColor,
          text: label,
        },

        title: title,
        options: {
          draggable: drag,
          animation: google.maps.Animation.BOUNCE,
          // icon: 'assets/layout/images/interface/maps/pin-move.svg',
          height: 60,
          width: 60,
        },
      });
    } else {
      console.log('A marker with the same position already exists.');
    }
  }

  getAddress(latitude, longitude) {
    this.geocoder
      .geocode({
        location: { lat: Number(latitude), lng: Number(longitude) },
      })
      .subscribe(({ results, status }) => {
        if (status === 'OK') {
          if (results[0]) {
            // this.mapOptions.zoom = 19;

            this.newAddress = {
              line1: '',
              line2: '',
              line3: '',
              line4: '',
              town: '',
              province: '',
              postalCode: '',
              id: '',
            };

            this.newAddress['line1'] = '';
            const streetAddress = {
              streetNumber: '',
              streetName: '',
            };
            this.newAddress['line2'] = '';
            this.newAddress['line3'] = results[0].formatted_address;
            if (results[0]?.plus_code?.global_code) {
              this.newAddress['line3'] =
                this.newAddress['line3'] +
                '; Global Code: ' +
                results[0]?.plus_code.global_code;
            }
            this.formattedAddress = results[0].formatted_address.split(',');
            const components = results[0].address_components;
            Object.keys(components).map((key, index) => {
              if (components[key].types?.indexOf('street_number') > -1) {
                streetAddress.streetNumber = components[key].long_name; // number
              }

              if (components[key].types?.indexOf('route') > -1) {
                streetAddress.streetName = components[key].long_name; // street
              }

              if (components[key].types?.indexOf('sublocality_level_1') > -1) {
                this.newAddress['line4'] = components[key].long_name; // suburb
              }
              if (components[key].types?.indexOf('locality') > -1) {
                this.newAddress['town'] = components[key].long_name;
              }
              if (
                components[key].types?.indexOf('administrative_area_level_1') >
                -1
              ) {
                this.newAddress['province'] = components[key].long_name;
              }
              if (components[key].types?.indexOf('postal_code') > -1) {
                this.newAddress['postalCode'] = components[key].long_name;
              }
            });

            if (streetAddress.streetName && streetAddress.streetNumber) {
              this.newAddress['line1'] =
                streetAddress.streetNumber + ' ' + streetAddress.streetName;
            } else {
              // we need clarification on the address it's potentially not available on googlemaps
            }

            this.newAddress = this.helperService.sanitizeParams(
              this.newAddress
            );

            this.emitPoint();
          } else {
            window.alert('No results found');
          }
        } else {
          window.alert('Geocoder failed due to: ' + status);
        }
      });
  }

  showDialogue() {
    this.help = true;
  }

  resetPolygon(event) {
    this.boundary = null;
    this.setPolygon.emit(false);
  }

  /**
   * Used to draw a shape onto the map
   * @param area Array of points you would like to draw onto the map
   */
  setArea(area) {
    const newArea = new google.maps.Polygon({
      paths: area.map((x) => ({ lat: x[1], lng: x[0] })),
      strokeColor: '#FF00FF',
      strokeOpacity: 0.8,
      strokeWeight: 3,
      fillColor: '#FF00FF',
      fillOpacity: 0.35,
    });

    newArea.setMap(this.googleMapInstance.googleMap);

    this.setPolygon.emit(area); // send back the array of coordinates
  }
}
